import { DragDropContext, Draggable, DraggableStateSnapshot, Droppable, DropResult } from "react-beautiful-dnd";
import { Box, List, ListItem, Typography } from "@mui/material";
import { DraggablePortalItem } from "./DraggablePortalItem";
import React, { useCallback, useMemo } from "react";
import { Coord, NamedCoords } from "./PositionsData";
import { usePathContext } from "../PathInfo";
import { useTreeModifyContext } from "../TreeDataContext";
import { PositionsEditorCoordCard } from "./PositionsEditorCoordCard";

export function CoordsArray(props: { coords: any }) {
  const { path } = usePathContext();
  const { saveChangesAtPath } = useTreeModifyContext();

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (
        result.source.droppableId === result.destination.droppableId &&
        result.source.index === result.destination.index
      )
        return;

      const items = props.coords.slice();
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      saveChangesAtPath(items, path);
    },
    [path, props.coords, saveChangesAtPath],
  );

  const allCoords = useMemo(() => {
    if (Array.isArray(props.coords)) {
      return props.coords as Coord[];
    }
    let coords: NamedCoords = {};
    Object.keys(props.coords).map((coordName, index) => {
      coords[coordName] = props.coords[coordName];
    });
    return coords;
  }, [props.coords]);

  const allCoordVisuals = useMemo(() => {
    if (Array.isArray(allCoords)) {
      return allCoords.map((dot, index) => {
        return (
          <Draggable
            key={`${allCoords[index].x}:${allCoords[index].y}`}
            draggableId={`${allCoords[index].x}:${allCoords[index].y}`}
            index={index}
          >
            {(provided: any, snapshot: DraggableStateSnapshot) => {
              return (
                <DraggablePortalItem snapshot={snapshot}>
                  <ListItem
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                    style={getStyle(provided.draggableProps.style, snapshot)}
                  >
                    <PositionsEditorCoordCard
                      key={index}
                      coordId={index}
                      x={allCoords[index].x}
                      y={allCoords[index].y}
                      allCoords={allCoords}
                    ></PositionsEditorCoordCard>
                  </ListItem>
                </DraggablePortalItem>
              );
            }}
          </Draggable>
        );
      });
    }
    return Object.keys(allCoords)
      .sort(sortCoordsByName)
      .map((coordName, index) => {
        return (
          <ListItem key={coordName}>
            <PositionsEditorCoordCard
              key={coordName}
              coordId={coordName}
              x={allCoords[coordName].x}
              y={allCoords[coordName].y}
              allCoords={allCoords}
            ></PositionsEditorCoordCard>
          </ListItem>
        );
      });
  }, [allCoords]);

  return (
    <Box key={"coords-array"}>
      <Typography variant="subtitle1">Coordinates:</Typography>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-coords">
          {(droppable: any) => (
            <List {...droppable.droppableProps} ref={droppable.innerRef} className="coords-list-container">
              {allCoordVisuals.map((coord) => coord)}
              {droppable.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
}

function sortCoordsByName(a: string, b: string) {
  return a.localeCompare(b);
}

function getStyle(style: any, snapshot: any) {
  if (!snapshot.isDropAnimating) {
    return {
      ...style,
      padding: 3,
    };
  }
  return {
    ...style,
    transitionDuration: `0.001s`, // cannot be 0
    padding: 3,
  };
}
